import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Button,
  CircularProgress,
  Alert,
  useTheme,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  alpha
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  MoreVert as MoreVertIcon,
  Receipt as ReceiptIcon,
  History as HistoryIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
// No need for translations in admin pages
import { useAdminService, User, UserPlan, UserLog } from '../../services/admin.service';
import { loginAsUser } from '../../utils/adminLoginUtils';
import UserPlansModal from './UserPlansModal';
import UserLogsModal from './UserLogsModal';
import { auth } from '../../config/firebase';

// Using the User interface from admin.service.ts

// Define sort directions and column types for type safety
type SortDirection = 'asc' | 'desc';
type SortableColumn = 'name' | 'email' | 'createdAt' | 'status' | 'role';

const UsersPage: React.FC = () => {
  // No translations for admin pages
  const theme = useTheme();
  const { getUsers, getUserPlans, getUserLogs, loginAsUser: getLoginToken } = useAdminService();
  
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<SortableColumn>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  
  // Menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [isLoggingInAsUser, setIsLoggingInAsUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  
  // Plans modal state
  const [plansModalOpen, setPlansModalOpen] = useState(false);
  const [userPlans, setUserPlans] = useState<UserPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState<string | null>(null);
  
  // Logs modal state
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const [userLogs, setUserLogs] = useState<UserLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState<string | null>(null);

  // Backup mock data in case the API fails
  const mockUsers: User[] = [
    {
      id: '1',
      email: 'john.doe@example.com',
      firstName: 'John',
      lastName: 'Doe',
      createdAt: '2023-01-15T10:30:00Z',
      isVerified: true,
      status: 'active',
      role: 'user'
    },
    {
      id: '2',
      email: 'jane.smith@example.com',
      firstName: 'Jane',
      lastName: 'Smith',
      createdAt: '2023-02-20T14:45:00Z',
      isVerified: true,
      status: 'active',
      role: 'admin'
    },
    {
      id: '3',
      email: 'bob.johnson@example.com',
      firstName: 'Bob',
      lastName: 'Johnson',
      createdAt: '2023-03-10T09:15:00Z',
      isVerified: false,
      status: 'pending',
      role: 'user'
    },
    {
      id: '4',
      email: 'alice.williams@example.com',
      firstName: 'Alice',
      lastName: 'Williams',
      createdAt: '2023-04-05T16:20:00Z',
      isVerified: true,
      status: 'inactive',
      role: 'user'
    },
    {
      id: '5',
      email: 'charlie.brown@example.com',
      firstName: 'Charlie',
      lastName: 'Brown',
      createdAt: '2023-05-12T11:10:00Z',
      isVerified: true,
      status: 'active',
      role: 'user'
    }
  ];

  // Fetch users from API
  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch real users from the backend
      const fetchedUsers = await getUsers();
      setUsers(fetchedUsers);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Error fetching users. Using mock data instead.');
      // Fallback to mock data if the API fails
      setUsers(mockUsers);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  // Menu handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, user: User) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  // Plans handlers
  const handleViewPlans = async () => {
    if (!selectedUser) return;

    handleMenuClose();
    setPlansModalOpen(true);
    setPlansLoading(true);
    setPlansError(null);

    try {
      const plans = await getUserPlans(selectedUser.id);
      setUserPlans(plans);
    } catch (err) {
      console.error('Error fetching user plans:', err);
      setPlansError('Error fetching user plans');
    } finally {
      setPlansLoading(false);
    }
  };

  const handleRefreshPlans = async () => {
    if (!selectedUser) return;

    setPlansLoading(true);
    setPlansError(null);

    try {
      const plans = await getUserPlans(selectedUser.id);
      setUserPlans(plans);
    } catch (err) {
      console.error('Error refreshing user plans:', err);
      setPlansError('Error refreshing user plans');
    } finally {
      setPlansLoading(false);
    }
  };

  const handleClosePlansModal = () => {
    setPlansModalOpen(false);
  };

  // Logs handlers
  const handleViewLogs = async () => {
    if (!selectedUser) return;
    
    handleMenuClose();
    setLogsModalOpen(true);
    setLogsLoading(true);
    setLogsError(null);
    
    try {
      const logs = await getUserLogs(selectedUser.id);
      setUserLogs(logs);
    } catch (err) {
      console.error('Error fetching user logs:', err);
      setLogsError('Error fetching user logs');
    } finally {
      setLogsLoading(false);
    }
  };

  const handleLoginAsUser = async () => {
    if (!selectedUser) return;
    if (!auth.currentUser) return;
    
    handleMenuClose();
    setIsLoggingInAsUser(true);
    setError(null);
    
    try {
      // Get custom token from the backend
      const customToken = await getLoginToken(selectedUser.id);
      const adminToken = await getLoginToken(auth.currentUser.uid);//We need this to log back in after we finish the support session
      // Login as the selected user using our utility function
      await loginAsUser(customToken, adminToken);
    } catch (err) {
      console.error('Error logging in as user:', err);
      setError(`Failed to login as ${selectedUser.firstName} ${selectedUser.lastName}`);
      setIsLoggingInAsUser(false);
    }
  };

  const handleCloseLogsModal = () => {
    setLogsModalOpen(false);
  };

  // Handle sorting column click
  const handleSortClick = (column: SortableColumn) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
    setPage(0);
  };

  const filteredUsers = users.filter(user => {
    const searchTermLower = searchTerm.toLowerCase();
    return (
      user.email.toLowerCase().includes(searchTermLower) ||
      user.firstName.toLowerCase().includes(searchTermLower) ||
      user.lastName.toLowerCase().includes(searchTermLower)
    );
  });

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'name':
        const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
        const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
        comparison = nameA.localeCompare(nameB);
        break;
      case 'email':
        comparison = a.email.toLowerCase().localeCompare(b.email.toLowerCase());
        break;
      case 'createdAt':
        comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        break;
      case 'status':
        comparison = a.status.localeCompare(b.status);
        break;
      case 'role':
        comparison = a.role.localeCompare(b.role);
        break;
      default:
        comparison = 0;
    }
    
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const paginatedUsers = sortedUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return {
          bg: theme.palette.success.main,
          text: theme.palette.success.contrastText
        };
      case 'inactive':
        return {
          bg: theme.palette.error.main,
          text: theme.palette.error.contrastText
        };
      case 'pending':
        return {
          bg: theme.palette.warning.main,
          text: theme.palette.warning.contrastText
        };
      default:
        return {
          bg: theme.palette.grey[500],
          text: theme.palette.getContrastText(theme.palette.grey[500])
        };
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 0 }
      }}>
        <Typography variant="h4" component="h1" sx={{ 
          fontWeight: 'bold',
          fontSize: { xs: '1.75rem', sm: '2.125rem' }
        }}>
          Users
        </Typography>
        
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<RefreshIcon />}
          onClick={fetchUsers}
          disabled={loading}
          sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}
        >
          Refresh
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Paper sx={{ width: '100%', mb: 2, overflow: 'hidden', borderRadius: '8px' }}>
        <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
          <TextField
            placeholder="Search users..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            variant="outlined"
            size="small"
            sx={{ 
              width: { xs: '100%', sm: '350px' },
              '.MuiOutlinedInput-root': {
                borderRadius: '6px'
              }
            }}
          />
        </Box>
        
        <TableContainer sx={{ 
          maxHeight: { xs: 'calc(100vh - 350px)', sm: 'calc(100vh - 300px)' },
          overflowX: 'auto'
        }}>
          <Table stickyHeader size="medium" sx={{ minWidth: { xs: 650, md: 800 } }}>
            <TableHead>
              <TableRow>
                <TableCell 
                  sx={{ 
                    width: { xs: '20%', md: '15%' },
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { backgroundColor: theme.palette.action.hover }
                  }}
                  onClick={() => handleSortClick('name')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Name
                    {sortBy === 'name' && (
                      <Box component="span" sx={{ ml: 0.5, display: 'flex', alignItems: 'center' }}>
                        {sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell 
                  sx={{ 
                    width: { xs: '30%', md: '25%' }, 
                    display: { xs: 'none', sm: 'table-cell' },
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { backgroundColor: theme.palette.action.hover }
                  }}
                  onClick={() => handleSortClick('email')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Email
                    {sortBy === 'email' && (
                      <Box component="span" sx={{ ml: 0.5, display: 'flex', alignItems: 'center' }}>
                        {sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell 
                  sx={{ 
                    width: { xs: '20%', md: '15%' }, 
                    display: { xs: 'none', md: 'table-cell' },
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { backgroundColor: theme.palette.action.hover }
                  }}
                  onClick={() => handleSortClick('createdAt')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Created At
                    {sortBy === 'createdAt' && (
                      <Box component="span" sx={{ ml: 0.5, display: 'flex', alignItems: 'center' }}>
                        {sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell 
                  sx={{ 
                    width: { xs: '15%', md: '10%' }, 
                    textAlign: 'center',
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { backgroundColor: theme.palette.action.hover }
                  }}
                  onClick={() => handleSortClick('status')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    Status
                    {sortBy === 'status' && (
                      <Box component="span" sx={{ ml: 0.5, display: 'flex', alignItems: 'center' }}>
                        {sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ width: { xs: '15%', md: '10%' }, textAlign: 'center', display: { xs: 'none', md: 'table-cell' } }}>Verified</TableCell>
                <TableCell 
                  sx={{ 
                    width: { xs: '15%', md: '10%' }, 
                    textAlign: 'center',
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { backgroundColor: theme.palette.action.hover }
                  }}
                  onClick={() => handleSortClick('role')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    Role
                    {sortBy === 'role' && (
                      <Box component="span" sx={{ ml: 0.5, display: 'flex', alignItems: 'center' }}>
                        {sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ width: { xs: '20%', md: '15%' }, textAlign: 'center' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={40} />
                      <Typography variant="body2" color="text.secondary">
                        Loading users...
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : paginatedUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      {searchTerm ? (
                        <>
                          <SearchIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                          <Typography variant="body1" color="text.secondary">
                            No search results found for "{searchTerm}"
                          </Typography>
                          <Button 
                            variant="text" 
                            size="small" 
                            onClick={() => setSearchTerm('')}
                          >
                            Clear search
                          </Button>
                        </>
                      ) : (
                        <>
                          <PeopleIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                          <Typography variant="body1" color="text.secondary">
                            No users found
                          </Typography>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedUsers.map((user) => {
                  const statusColor = getStatusColor(user.status);
                  
                  return (
                    <TableRow key={user.id} hover>
                      <TableCell sx={{ verticalAlign: 'middle' }}>
                        {user.firstName} {user.lastName}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, verticalAlign: 'middle' }}>{user.email}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, verticalAlign: 'middle' }}>{formatDate(user.createdAt)}</TableCell>
                      <TableCell sx={{ textAlign: 'center', verticalAlign: 'middle' }}>
                        <Chip
                          label={user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                          size="small"
                          sx={{
                            backgroundColor: statusColor.bg,
                            color: statusColor.text,
                            fontWeight: 'bold',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, textAlign: 'center', verticalAlign: 'middle' }}>
                        {user.isVerified ? (
                          <CheckCircleIcon color="success" fontSize="small" />
                        ) : (
                          <CancelIcon color="error" fontSize="small" />
                        )}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center', verticalAlign: 'middle' }}>
                        <Chip
                          label={user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                          size="small"
                          variant="outlined"
                          color={user.role === 'admin' ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center', verticalAlign: 'middle' }}>
                        <Tooltip title="User options">
                          <IconButton 
                            size="small" 
                            onClick={(e) => handleMenuOpen(e, user)}
                            sx={{ '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) } }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredUsers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={window.innerWidth < 600 ? 'Rows:' : 'Rows per page:'}
          sx={{
            '.MuiTablePagination-selectLabel': {
              margin: { xs: 0, sm: 2 }
            },
            '.MuiTablePagination-displayedRows': {
              margin: { xs: 0, sm: 2 }
            }
          }}
        />
      </Paper>
      
      {/* User options menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleViewPlans}>
          <ListItemIcon>
            <ReceiptIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Plans</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleViewLogs}>
          <ListItemIcon>
            <HistoryIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Activity Logs</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={handleLoginAsUser}
          disabled={isLoggingInAsUser || (selectedUser ? !selectedUser.isVerified : false)}
        >
          <ListItemIcon>
            {isLoggingInAsUser ? (
              <CircularProgress size={20} />
            ) : (
              <PersonAddIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            {selectedUser && !selectedUser.isVerified 
              ? 'User email not verified' 
              : 'Login as User'}
          </ListItemText>
        </MenuItem>
      </Menu>
      
      {/* User Plans Modal */}
      <UserPlansModal
        open={plansModalOpen}
        onClose={handleClosePlansModal}
        userName={selectedUser ? `${selectedUser.firstName} ${selectedUser.lastName}` : ''}
        userId={selectedUser?.id || ''}
        userPlans={userPlans}
        loading={plansLoading}
        error={plansError}
        onRefreshPlans={handleRefreshPlans}
      />
      
      {/* User Logs Modal */}
      <UserLogsModal
        open={logsModalOpen}
        onClose={handleCloseLogsModal}
        userName={selectedUser ? `${selectedUser.firstName} ${selectedUser.lastName}` : ''}
        userLogs={userLogs}
        loading={logsLoading}
        error={logsError}
      />
    </Box>
  );
};

export default UsersPage;
